import { runWithDbRequestContext } from "@crm/db/request-context";
import { serverEffectRuntime } from "@crm/effect/server/runtime";
import { formatServerTimingMetric } from "@crm/effect/server-trace";
import { handleServerError } from "@crm/errors/server-handler";
import { generateId } from "@crm/kernel/id-generator";
import { initSentry } from "@crm/shared/sentry";
import { createMiddleware, createStart } from "@tanstack/react-start";
import { Data, Effect } from "effect";
import { startRuntime } from "@/start-runtime";

class ServerFunctionNextError extends Data.TaggedError("ServerFunctionNextError")<{
  readonly cause: unknown;
}> {}

/**
 * TanStack Start 全局配置
 *
 * 这个文件用于配置全局中间件,包括:
 * - 模拟延迟中间件 (simulateLatencyMiddleware) - 用于测试性能优化效果
 * - 认证中间件 (requireAuth) - 为所有请求提供认证上下文
 * - 错误处理 (Sentry) - 自动捕获错误和异常
 * - 性能监控 (Sentry) - 追踪性能指标
 *
 * 中间件执行顺序:
 * 1. requestMiddleware - 所有请求 (server routes, SSR, server functions)
 * 2. functionMiddleware - 仅 server functions
 */

/**
 * 模拟延迟中间件
 *
 * 用于在本地开发时模拟远程部署的网络延迟,方便测试性能优化效果
 *
 * 配置方式:
 * - 环境变量 SIMULATE_LATENCY=true 启用
 * - 环境变量 LATENCY_MS=500 设置延迟毫秒数 (默认 300ms)
 *
 * 使用场景:
 * 1. 测试 prefetchQuery 优化效果
 * 2. 验证骨架屏显示时机
 * 3. 对比优化前后的用户体验
 */
const simulateLatencyMiddleware = createMiddleware().server(({ next, request }) => {
  return runWithDbRequestContext(async () => {
    // 只在开发环境且显式启用时生效
    const { latencyMs, simulateLatency } = startRuntime.config;

    if (simulateLatency) {
      const url = new URL(request.url);

      // 只对 server functions 和 API routes 添加延迟
      // 静态资源 (/_build/*, /assets/*) 不受影响
      const shouldDelay = !(
        url.pathname.startsWith("/_build/") ||
        url.pathname.startsWith("/assets/") ||
        url.pathname.endsWith(".css") ||
        url.pathname.endsWith(".js") ||
        url.pathname.endsWith(".map")
      );

      if (shouldDelay) {
        await serverEffectRuntime.runPromise(startRuntime.sleep(latencyMs));
      }
    }

    return next();
  });
});

const serverTimingMiddleware = createMiddleware().server(async ({ next }) => {
  const startedAt = performance.now();
  const result = await next();
  const totalMetric = formatServerTimingMetric({
    handler: "request",
    phase: "total",
    durationMs: performance.now() - startedAt,
  });
  const current = result.response.headers.get("Server-Timing");
  result.response.headers.set(
    "Server-Timing",
    current ? `${current}, ${totalMetric}` : totalMetric,
  );
  return result;
});

const requestContextMiddleware = createMiddleware().server(({ next }) => {
  const requestId = generateId();
  return next({ context: { requestId } });
});

const serverFunctionErrorMiddleware = createMiddleware({
  type: "function",
}).server(({ next, context }) => {
  const requestId = (context as unknown as { requestId: string }).requestId;

  return serverEffectRuntime
    .runPromise(
      Effect.tryPromise({
        try: () => next(),
        catch: (cause) => new ServerFunctionNextError({ cause }),
      }).pipe(
        Effect.match({
          onFailure: (error) => ({ _tag: "failure" as const, error }),
          onSuccess: (value) => ({ _tag: "success" as const, value }),
        }),
      ),
    )
    .then((result) =>
      result._tag === "failure" ? handleServerError(result.error.cause, requestId) : result.value,
    );
});

export const startInstance = createStart(() => {
  // 初始化 Sentry 错误监控
  // Sentry SDK 会自动捕获未处理的错误和异常
  initSentry();

  return {
    // 全局请求中间件 - 应用于所有请求
    requestMiddleware: [
      requestContextMiddleware,
      serverTimingMiddleware,
      simulateLatencyMiddleware,
    ],
    // ServerFn 的唯一 unknown → HTTP 终态分类边界
    functionMiddleware: [serverFunctionErrorMiddleware],
  };
});
